import React, { useState } from 'react';
import { Button } from '@/components/ui';
import { CloseIcon, ChevronDownIcon } from '@/components/icons';

interface InviteMemberModalProps {
    isOpen: boolean;
    onClose: () => void;
    onInvite: (data: { firstName: string; lastName: string; email: string; role: string }) => void;
    isInviting?: boolean;
}

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({ isOpen, onClose, onInvite, isInviting = false }) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('');

    const isFormValid = firstName.trim() !== '' && lastName.trim() !== '' && email.trim() !== '' && role !== '';

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!isFormValid) return;

        onInvite({
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
            role
        });
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
        >
            <div className="bg-white rounded-xl shadow-lg w-full max-w-lg m-4" onClick={(e) => e.stopPropagation()}>
                <div className="p-8">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800">Invite a Member</h2>
                            <p className="text-sm text-gray-500 mt-1">Invite members to manage task.</p>
                        </div>
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-800" aria-label="Close modal">
                            <CloseIcon className="w-6 h-6" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="first-name" className="block text-sm font-semibold text-gray-700 mb-1">First Name</label>
                                <input
                                    type="text"
                                    id="first-name"
                                    placeholder="Enter First Name"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-0 focus:border-gray-300 bg-[#FAFAFA]"
                                />
                            </div>
                            <div>
                                <label htmlFor="last-name" className="block text-sm font-semibold text-gray-700 mb-1">Last Name</label>
                                <input
                                    type="text"
                                    id="last-name"
                                    placeholder="Enter Last Name"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-0 focus:border-gray-300 bg-[#FAFAFA]"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1">Email Address</label>
                                <input
                                    type="email"
                                    id="email"
                                    placeholder="eg, shola@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-0 focus:border-gray-300 bg-[#FAFAFA]"
                                />
                            </div>
                            <div>
                                <label htmlFor="role" className="block text-sm font-semibold text-gray-700 mb-1">Role</label>
                                <div className="relative">
                                    <select
                                        id="role"
                                        value={role}
                                        onChange={(e) => setRole(e.target.value)}
                                        className="w-full appearance-none bg-[#FAFAFA] px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-0 focus:border-gray-300 pr-8"
                                    >
                                        <option value="" disabled>Select option</option>
                                        <option value="ADMIN_STAFF">Administrator</option>
                                        <option value="EDITOR">Editor</option>
                                        <option value="VIEWER">Viewer</option>
                                    </select>
                                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                        <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={!isFormValid || isInviting}
                            className="w-full"
                        >
                            {isInviting ? 'Sending...' : 'Send Invite'}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default InviteMemberModal;
